<template>
	<div class="frame-assembly-wrapper" :class="{'is-mobile': sMobile}">
		<div :class="['frame d-flex flex-column align-center', !sMobile ? 'pa-5' : 'pt-5 px-3']">
			<div class="d-flex flex-column">
				<span class="font-weight-bold pb-3">{{ $t('frameAssembly.frameType') }}</span>
				<div class="d-flex flex-row justify-md-space-between align-center flex-wrap flex-gap">
					<ItemCardFrameAssemblyTypeSelection class="flex-grow-1" :frame-type="internalModel.frameType" :small="sMobile" @changed="internalModel.frameType = $event"/>
					<AsiBtn @click="priceTableDialog.open()" :block="sMobile" outlined color="primary">
						{{ $t('frameAssembly.terms.priceTable') }}
					</AsiBtn>
				</div>
			</div>
			<ItemCardFrameAssemblyFrame :type="internalModel.frameType" :width="+internalModel.width" :height="+internalModel.height"  @changed="onFrameTypeChanged"/>
		</div>
		<v-divider class="divider" vertical/>
		<div :class="['form', !sMobile ? 'pa-5' : 'pt-5']">
			<v-container>
				<v-row>
					<v-col :cols="sMobile ? 12 : 6">
						<span class="font-weight-bold pb-3">{{ $t('frameAssembly.width') }}</span>
						<AsiTextFieldSimple :disabled="isFrameTypeCorner()" v-model="internalModel.width" type="number" class="mt-3" :rules="internalModel?.rules('width')"/>
					</v-col>
					<v-col :cols="sMobile ? 12 : 6">
						<span class="font-weight-bold pb-3">{{ $t('frameAssembly.height') }}</span>
						<AsiTextFieldSimple :disabled="isFrameTypeCorner()" v-model="internalModel.height" type="number" class="mt-3" :rules="internalModel?.rules('height')" />
					</v-col>
				</v-row>
				<v-row>
					<v-col :cols="sMobile ? 12 : 6">
										<span class="font-weight-bold pb-3">{{
												$t('frameAssembly.installation')
											}}</span>
						<AsiRadioGroup v-model="internalModel.installation">
							<v-radio :label="$t('constants.frame')" :value="frameAssemblyInstallation.frame"/>
							<v-radio :label="$t('constants.wing')" :value="frameAssemblyInstallation.wing"/>
						</AsiRadioGroup>
					</v-col>
					<v-col :cols="sMobile ? 12 : 6">
										<span class="font-weight-bold pb-3">{{
												$t('frameAssembly.measuringPoint')
											}}</span>
						<AsiRadioGroup v-model="internalModel.measuringPoint">
							<v-radio :label="$t('constants.rebate')" :value="frameAssemblyMeasuringPoint.rebate"/>
							<v-radio :label="$t('constants.light')" :value="frameAssemblyMeasuringPoint.light"/>
						</AsiRadioGroup>
					</v-col>
				</v-row>

				<v-row>
					<v-col :cols="sMobile ? 12 : 6">
						<span class="font-weight-bold pb-3">{{ $t('cartPosition.positionNr') }}</span>
						<AsiTextFieldSimple v-model="internalModel.positionNr" class="mt-3"
						                    :rules="positionNrRule"
						                    :counter="12"
						                    :label="$t('cartPosition.positionNr')"
						                    :palceholder="$t('cartPosition.positionNr')"/>
					</v-col>
				</v-row>

				<v-spacer />

				<v-row justify="space-between">
					<v-col align-self="end" class="d-flex flex-column" cols="auto">
						<ItemQuantitySelection v-model="internalModel.quantity" :unit="itemSalesUnitOfMeasure" class="mr-3" is-cart-selector :step=1 :default-value=1 />
					</v-col>
				</v-row>

			</v-container>
		</div>

		<FrameAssemblyPriceTableDialog v-if="priceTableDialog.isLoaded" :open="priceTableDialog.isOpen"
		                               @cancel="priceTableDialog.close()" @save="priceTableDialog.close(); change();"/>
	</div>
</template>

<script lang="ts">
	import {Component, Emit, Prop, Watch} from "vue-property-decorator";
	import AsiBtn from "@/components/common/AsiBtn.vue";
	import ItemCardFrameAssemblyTypeSelection from "@/components/item/shop/ItemCardFrameAssemblyTypeSelection.vue";
	import ItemCardFrameAssemblyFrame from "@/components/item/shop/ItemCardFrameAssemblyFrame.vue";
	import AsiRadioGroup from "@/components/common/AsiRadioGroup";
	import AsiTextareaSimple from "@/components/common/AsiTextareaSimple";
	import AsiTextFieldSimple from "@/components/common/AsiTextFieldSimple";
	import ItemPriceTable from "@/components/item/ItemPriceTable.vue";
	import ItemQuantitySelection from "@/components/item/shop/ItemQuantitySelection.vue";
	import {mixins} from "vue-class-component";
	import ResponsiveChecks from "@/mixins/ResponsiveChecks.vue";
	import Icon from "@/plugins/icons";
	import {
		FrameAssemblyInstallation,
		FrameAssemblyMeasuringPoint,
		FrameAssemblyType,
		UnitOfMeasure
	} from "@/helpers/constants";
	import CartSelectInput from "@/components/cart/shop/CartSelectInput.vue";
	import FrameAssemblyRules from "@/models/frame-assembly/FrameAssemblyRules";
	import {ValidationRule} from "@/declarations/ValidationRule";
	import FrameAssemblyCreate from "@/models/frame-assembly/FrameAssemblyCreate";
	import FrameAssemblyUpdate from "@/models/frame-assembly/FrameAssemblyUpdate";
	import FrameAssemblyPriceTableDialog from "@/components/frame-assembly/FrameAssemblyPriceTableDialog.vue";
	import DialogHandler from "@/components/common/DialogHandler";

	@Component({
		components: {
			FrameAssemblyPriceTableDialog,
			CartSelectInput,
			ItemQuantitySelection,
			ItemPriceTable,
			AsiTextFieldSimple,
			AsiTextareaSimple,
			AsiRadioGroup,
			ItemCardFrameAssemblyFrame,
			ItemCardFrameAssemblyTypeSelection,
			AsiBtn
		}
	})
	export default class FrameAssemblyConfigurator extends mixins(ResponsiveChecks) {
		private icons = Icon;
		private selectedCartId: string | null = null;
		private priceTableDialog: DialogHandler = new DialogHandler();

		@Prop({type: Object, required: true})
		public value!: FrameAssemblyCreate | FrameAssemblyUpdate;

		@Prop({type: Number, default: FrameAssemblyType.frame})
		public frameType!: FrameAssemblyType;

		private internalModel: FrameAssemblyCreate | FrameAssemblyUpdate | null = null;

		private frameAssemblyInstallation = FrameAssemblyInstallation;
		private frameAssemblyMeasuringPoint = FrameAssemblyMeasuringPoint;

		@Watch('value', {deep: true, immediate: true})
		private onValueChanged(): void {
			this.internalModel = this.value;
		}

		@Watch('internalModel', {deep: true})
		private emitModelUpdate(): void {
			if (this.internalModel?.frameType === FrameAssemblyType.corner) this.preConfigureCorner();
			this.$emit('input', this.internalModel);
			this.$emit('changed', this.internalModel);
			this.emitPositionNrValidation();
		}

		@Emit('position-nr-validation')
		private emitPositionNrValidation(): boolean {
			return this.validatePositionNrInput();
		}

		private onFrameTypeChanged(): void {
			this.emitModelUpdate();
		}

		private get itemSalesUnitOfMeasure(): UnitOfMeasure {
			return UnitOfMeasure.unitPiece;
		}

		private get positionNrRule(): ValidationRule[] {
			return FrameAssemblyRules.positionNrRules;
		}

		private isFrameTypeCorner(): boolean {
			return this.internalModel?.frameType === FrameAssemblyType.corner;
		}

		private preConfigureCorner() {
			if (!this.internalModel) return;
			this.internalModel.width = 100;
			this.internalModel.height = 100;
		}

		private validatePositionNrInput() {
			if (this.internalModel === null || this.internalModel.positionNr === null) return true;
			const positionNr = this.internalModel.positionNr;
			return this.positionNrRule.every(rule => rule(positionNr) == true);
		}
	}
</script>

<style lang="scss" scoped>
	.frame-assembly-wrapper {
		width: 100%;
		display: grid;

		&:not(.is-mobile) {
			grid-template-areas:    'frame divider form';
			grid-template-columns: 1fr auto 1fr;
		}

		&.is-mobile {
			grid-template-areas:    'frame'
								'divider'
								'form';
			grid-template-columns: 100%;
		}

		.frame {
			grid-area: frame;
			max-width: 100%;

			> div {
				width: 100%;
			}
		}

		.divider {
			grid-area: divider;
		}

		.form {
			grid-area: form;
			max-width: 100%;
		}

		.flex-gap {
			gap: 1rem;
		}
	}
</style>
